import React, { useState } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import Icon from '../AppIcon';
import Button from './Button';
import { useAuth } from '../../contexts/AuthContext';
import { hasPermission } from '../../utils/permissions';

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, userRoles, isSuperAdmin } = useAuth();
  const [isUpgradeCardVisible, setIsUpgradeCardVisible] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  // Add this function - toggleSection handler
  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev?.[sectionKey]
    }));
  };

  // Define navigationItems BEFORE using it
  const navigationItems = [
    {
      title: 'Main',
      items: [
        { name: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
        { name: 'Accounts', path: '/accounts', icon: 'Building2', permission: { feature: 'accounts', level: 'read' } },
        { name: 'Contacts', path: '/contacts', icon: 'Users', permission: { feature: 'contacts', level: 'read' } },
        { name: 'Deals', path: '/deals', icon: 'TrendingUp', permission: { feature: 'deals', level: 'read' } },
        { name: 'Pipeline', path: '/pipeline', icon: 'GitBranch', permission: { feature: 'pipeline', level: 'read' } },
      ],
    },
    {
      title: 'Customer Management',
      items: [
        { name: 'Customers', path: '/customer-management', icon: 'Users', permission: { feature: 'customers', level: 'read' } },
        { name: 'Calendar', path: '/calendar', icon: 'Calendar', permission: { feature: 'calendar', level: 'read' } },
        { name: 'Services', path: '/service-configuration', icon: 'Settings', permission: { feature: 'services', level: 'read' } },
      ],
    },
    {
      title: 'Financial',
      items: [
        { name: 'Invoices', path: '/invoice-list', icon: 'FileText', permission: { feature: 'invoices', level: 'read' } },
        { name: 'Billing', path: '/billing', icon: 'CreditCard', permission: { feature: 'billing', level: 'read' } },
      ],
    },
    {
      title: 'Communication',
      items: [
        { name: 'Emails', path: '/emails', icon: 'Mail', permission: { feature: 'emails', level: 'read' } },
        { name: 'Activities', path: '/activities', icon: 'Activity', permission: { feature: 'activities', level: 'read' } },
        { name: 'Notifications', path: '/notifications', icon: 'Bell', permission: { feature: 'notifications', level: 'read' } },
      ],
    },
    {
      title: 'Analytics',
      items: [
        { name: 'Reports', path: '/reports', icon: 'BarChart3', permission: { feature: 'reports', level: 'read' } },
      ],
    },
    {
      title: 'Administration',
      items: [
        { name: 'User Management', path: '/user-management', icon: 'UserCog', permission: { feature: 'users', level: 'read' } },
        { name: 'Role Management', path: '/role-management-dashboard', icon: 'Shield', permission: { feature: 'users', level: 'admin' } },
        { name: 'Organization Admin', path: '/organization-admin-panel', icon: 'Building', permission: { feature: 'users', level: 'admin' } },
        { name: 'Sample Data', path: '/sample-data-management-dashboard', icon: 'Database', superAdminOnly: true },
        { name: 'Settings', path: '/settings', icon: 'Settings', permission: { feature: 'settings', level: 'read' } },
        { name: 'Integrations', path: '/integrations', icon: 'Plug', permission: { feature: 'integrations', level: 'read' } },
      ],
    },
    {
      title: 'System Monitoring',
      items: [
        { name: 'System Audit', path: '/system-audit-dashboard', icon: 'Activity', superAdminOnly: true },
        { name: 'Error Monitoring', path: '/error-monitoring-center', icon: 'AlertTriangle', superAdminOnly: true },
        { name: 'Connectivity', path: '/system-connectivity-dashboard', icon: 'Wifi', superAdminOnly: true },
      ],
    },
  ];

  // Filter navigation items based on permissions
  const getFilteredNavigation = () => {
    return navigationItems?.map(section => ({
      ...section,
      items: section?.items?.filter(item => {
        // Super Admin only items
        if (item?.superAdminOnly && !isSuperAdmin) return false;
        
        // No permission check needed
        if (!item?.permission) return true;
        
        // Check if user has required permission
        return hasPermission(userRoles, item?.permission?.feature, item?.permission?.level);
      })
    }))?.filter(section => section?.items?.length > 0); // Remove empty sections
  };

  const filteredNavigation = getFilteredNavigation();

  const handleNavigation = (path) => {
    navigate(path);
    if (onClose) {
      onClose();
    }
  };

  const handleUpgradeClick = () => {
    navigate('/billing');
    if (onClose) {
      onClose();
    }
  };

  const handleUpgradeClose = () => {
    setIsUpgradeCardVisible(false);
  };

  // Render navigation item
  const renderNavItem = (item, isChild = false) => {
    const isActive = location?.pathname === item?.path;
    
    return (
      <button
        key={item?.path}
        onClick={() => handleNavigation(item?.path)}
        className={`
          w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium rounded-lg
          transition-smooth group
          ${isChild ? 'ml-4' : ''}
          ${isActive
            ? 'bg-primary text-primary-foreground shadow-sm'
            : 'text-muted-foreground hover:text-foreground hover:bg-muted'
          }
        `}
        aria-current={isActive ? 'page' : undefined}
      >
        <div className="flex items-center space-x-3">
          <Icon
            name={item?.icon}
            size={18}
            className={`
              ${isActive ? 'text-primary-foreground' : 'text-muted-foreground group-hover:text-foreground'}
            `}
          />
          <span>{item?.name}</span>
        </div>
        {item?.badge && (
          <span
            className={`
              px-2 py-0.5 text-xs font-medium rounded-full
              ${isActive
                ? 'bg-primary-foreground/20 text-primary-foreground'
                : 'bg-accent text-accent-foreground'
              }
            `}
          >
            {item?.badge}
          </span>
        )}
      </button>
    );
  };

  // Render collapsible parent section
  const renderParentSection = (title, icon, items, sectionKey, isExpanded) => {
    return (
      <div className="space-y-1">
        <button
          onClick={() => toggleSection(sectionKey)}
          className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-semibold rounded-lg text-foreground hover:bg-muted transition-smooth group"
        >
          <div className="flex items-center space-x-3">
            <Icon name={icon} size={18} className="text-muted-foreground group-hover:text-foreground" />
            <span>{title}</span>
          </div>
          {isExpanded ? (
            <ChevronUp size={16} className="text-muted-foreground" />
          ) : (
            <ChevronDown size={16} className="text-muted-foreground" />
          )}
        </button>
        {isExpanded && (
          <div className="space-y-1 pl-2">
            {items?.map(item => renderNavItem(item, true))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      {/* Sidebar */}
      <aside
        className={`
          fixed top-0 left-0 h-full w-64 bg-background border-r border-border z-50 lg:z-30
          transform transition-transform duration-300 ease-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="Zap" size={20} color="white" />
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-lg font-semibold text-foreground">LocalProCRM</span>
                <span className="px-2 py-0.5 text-xs font-medium bg-accent text-accent-foreground rounded-full">
                  v2.1
                </span>
              </div>
            </div>
            
            {/* Close button for mobile */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="lg:hidden"
              aria-label="Close navigation menu"
            >
              <Icon name="X" size={20} />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-4">
            <div className="px-3 space-y-4">
              {filteredNavigation?.map((section, idx) => (
                <div key={idx}>
                  <div className="px-3 mb-2">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {section?.title}
                    </h3>
                  </div>
                  <nav className="space-y-1">
                    {section?.items?.map((item) => (
                      <Link
                        key={item?.name}
                        to={item?.path}
                        onClick={() => onClose?.()}
                        className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          location?.pathname === item?.path
                            ? 'bg-primary text-white' :'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        <Icon name={item?.icon} size={18} className="mr-3" />
                        {item?.name}
                      </Link>
                    ))}
                  </nav>
                </div>
              ))}
            </div>
          </nav>

          {/* Upgrade Card */}
          {isUpgradeCardVisible && (
            <div className="p-4 border-t border-border">
              <div className="relative bg-gradient-to-br from-primary to-secondary p-4 rounded-lg text-primary-foreground">
                <button
                  onClick={handleUpgradeClose}
                  className="absolute top-2 right-2 p-1 rounded-full hover:bg-white/20 transition-smooth"
                  aria-label="Close upgrade card"
                >
                  <Icon name="X" size={14} />
                </button>
                
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Icon name="Crown" size={20} />
                    <span className="font-semibold">Upgrade to Pro</span>
                  </div>
                  
                  <p className="text-sm text-primary-foreground/90">
                    Unlock advanced analytics, unlimited contacts, and premium integrations.
                  </p>
                  
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleUpgradeClick}
                    className="w-full bg-white/20 hover:bg-white/30 text-primary-foreground border-white/30"
                  >
                    <Icon name="ArrowRight" size={16} className="mr-2" />
                    Upgrade Now
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="p-4 border-t border-border">
            <div className="text-xs text-muted-foreground text-center">
              © 2025 LocalProCRM. All rights reserved.
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;